import type { DriveArtifactItem, DriveDomainSection, DriveSubdomainNode } from './types';
import { normalizeScopeIdKey, scopeIdsEqual } from '../../../utils/scopeIdPlane';

/** Canonical demand pipeline stages (contact → situation → deal). */
export const DEMAND_STAGE_LANE_IDS = ['contact', 'situation', 'deal'] as const;

export type DemandStageKey = (typeof DEMAND_STAGE_LANE_IDS)[number];

export interface DemandStageCount {
  laneId: string;
  laneName: string;
  count: number;
  /** Drop-off vs prior stage (0–100). Undefined when prior count is zero. */
  dropOffPct?: number;
  /** Bar width 0–100 relative to max stage count. */
  barWidthPct: number;
}

function stageKeyFromText(text: string): DemandStageKey | undefined {
  const lower = text.toLowerCase();
  if (lower.includes('deal')) return 'deal';
  if (lower.includes('situation')) return 'situation';
  if (lower.includes('contact')) return 'contact';
  return undefined;
}

/** Exact grandchild ids contact/situation/deal in order (legacy fixture). */
export function isDemandStageSubdomain(subdomain: DriveSubdomainNode): boolean {
  const laneIds = (subdomain.grandchildren ?? []).map((lane) => lane.id.toLowerCase());
  if (laneIds.length < DEMAND_STAGE_LANE_IDS.length) return false;
  return DEMAND_STAGE_LANE_IDS.every((id, index) => laneIds[index] === id);
}

function artifactMatchesLane(
  item: DriveArtifactItem,
  laneId: string,
  grandchildIds: Set<string>
): boolean {
  if (scopeIdsEqual(item.lane, laneId)) return true;
  if (scopeIdsEqual(item.subdomainId, laneId) || scopeIdsEqual(item.filterScope, laneId)) {
    return true;
  }
  const inGrand = (raw: string | undefined) => {
    const key = normalizeScopeIdKey(raw);
    return Boolean(key && grandchildIds.has(key));
  };
  if (inGrand(item.subdomainId) || inGrand(item.filterScope) || inGrand(item.lane)) return true;
  return false;
}

function buildStagesFromLaneRows(
  rows: Array<{ laneId: string; laneName: string; count: number }>
): DemandStageCount[] {
  const maxCount = Math.max(1, ...rows.map((row) => row.count));
  return rows.map((row, index) => {
    const prev = index > 0 ? rows[index - 1]!.count : undefined;
    const dropOffPct =
      prev != null && prev > 0 ? Math.round((1 - row.count / prev) * 100) : undefined;
    return {
      ...row,
      dropOffPct,
      barWidthPct: Math.round((row.count / maxCount) * 100),
    };
  });
}

export function deriveDemandStageCounts(
  subdomain: DriveSubdomainNode,
  items: DriveArtifactItem[]
): DemandStageCount[] | null {
  if (!isDemandStageSubdomain(subdomain)) return null;
  const lanes = subdomain.grandchildren ?? [];
  const raw = lanes.map((lane) => ({
    laneId: lane.id,
    laneName: lane.name,
    count: items.filter((item) => item.lane === lane.id).length,
  }));
  return buildStagesFromLaneRows(raw);
}

/**
 * Live Central shape: contact / situation / deal are typed cluster columns (or deal leaves),
 * not fixed grandchild ids on a single subdomain.
 */
export function resolveDemandStageLanes(
  candidates: DriveSubdomainNode[]
): Array<{ stage: DemandStageKey; lane: DriveSubdomainNode }> | null {
  const byStage = new Map<DemandStageKey, DriveSubdomainNode>();
  for (const lane of candidates) {
    const key = stageKeyFromText(lane.id) ?? stageKeyFromText(lane.name);
    if (!key) continue;
    if (!byStage.has(key)) byStage.set(key, lane);
  }
  if (!DEMAND_STAGE_LANE_IDS.every((id) => byStage.has(id))) return null;
  return DEMAND_STAGE_LANE_IDS.map((stage) => ({ stage, lane: byStage.get(stage)! }));
}

export function deriveDemandStageCountsFromLanes(
  stageLanes: Array<{ stage: DemandStageKey; lane: DriveSubdomainNode }>,
  items: DriveArtifactItem[]
): DemandStageCount[] {
  const raw = stageLanes.map(({ lane }) => {
    const grandchildIds = new Set(
      (lane.grandchildren ?? [])
        .map((g) => normalizeScopeIdKey(g.id))
        .filter(Boolean)
    );
    return {
      laneId: lane.id,
      laneName: lane.name,
      count: items.filter((item) => artifactMatchesLane(item, lane.id, grandchildIds)).length,
    };
  });
  return buildStagesFromLaneRows(raw);
}

/** Prefer section.laneIds typed columns; fall back to legacy grandchild detection. */
export function deriveDemandStageCountsForDriveLane(
  lane: DriveSubdomainNode,
  allSubdomains: DriveSubdomainNode[],
  sections: DriveDomainSection[] | undefined,
  items: DriveArtifactItem[]
): DemandStageCount[] | null {
  const legacy = deriveDemandStageCounts(lane, items);
  if (legacy) return legacy;

  const section = (sections ?? []).find(
    (s) => s.id === lane.domainSectionId || (s.laneIds ?? []).includes(lane.id)
  );
  if (!section) return null;

  const typedIds = new Set(section.laneIds ?? []);
  const candidates = allSubdomains.filter(
    (sub) => typedIds.has(sub.id) || sub.domainSectionId === section.id
  );
  // Include deal-like leaves that may lack domainSectionId.
  for (const sub of allSubdomains) {
    if (stageKeyFromText(sub.id) === 'deal' || stageKeyFromText(sub.name) === 'deal') {
      candidates.push(sub);
    }
  }

  const resolved = resolveDemandStageLanes(candidates);
  if (!resolved) return null;
  // Only show funnel on the contact-stage column (or first stage lane) to avoid duplicates.
  if (lane.id !== resolved[0]!.lane.id) return null;
  return deriveDemandStageCountsFromLanes(resolved, items);
}

/** Stage with the largest drop-off from its predecessor (flow constraint). */
export function demandConstraintStageIndex(stages: DemandStageCount[]): number | null {
  let maxDrop = -1;
  let idx: number | null = null;
  for (let i = 1; i < stages.length; i++) {
    const drop = stages[i]?.dropOffPct;
    if (drop == null) continue;
    if (drop > maxDrop) {
      maxDrop = drop;
      idx = i;
    }
  }
  return idx;
}

export function demandStageSummaryLabel(stages: DemandStageCount[]): string {
  if (stages.length === 0) return 'No demand stages';
  return stages.map((s) => `${s.laneName} ${s.count}`).join(' → ');
}
